import { failingFetcher } from '@lib/http/fetch';
import type {
  LookupProductMembershipResponse,
  ProductMembership,
} from '@lib/types/api/ProductListV2';
import { useCallback, useMemo, useRef } from 'react';
import useSWR from 'swr';

const EMPTY_MEMBERSHIP: ProductMembership = {
  personal: [],
  organization: [],
};

const LOOKUP_CHUNK_SIZE = 50;

type MembershipMap = Record<string, ProductMembership>;

const buildLookupUrl = (encodedIds: string, organizationId?: string) => {
  const base = `/api/lists/products/lookup?product-ids=${encodedIds}`;
  return organizationId ? `${base}&org-id=${organizationId}` : base;
};

type ChunkedMembership = {
  // Membership for ids whose chunk resolved. Ids in a failed chunk are absent.
  merged: MembershipMap;
  // Ids whose chunk rejected — left unresolved so they stay loading and retry.
  failedIds: string[];
};

async function fetchMembershipChunked(
  productIds: string[],
  organizationId?: string
): Promise<ChunkedMembership> {
  const chunks: string[][] = [];
  for (let i = 0; i < productIds.length; i += LOOKUP_CHUNK_SIZE) {
    chunks.push(productIds.slice(i, i + LOOKUP_CHUNK_SIZE));
  }

  // allSettled (not all): one chunk failing must not discard the ids that
  // other chunks fetched successfully.
  const results = await Promise.allSettled(
    chunks.map((chunk) => {
      const ids = chunk.map(encodeURIComponent).join(',');
      return failingFetcher(
        buildLookupUrl(ids, organizationId)
      ) as Promise<LookupProductMembershipResponse>;
    })
  );

  const merged: MembershipMap = {};
  const failedIds: string[] = [];
  results.forEach((result, i) => {
    const chunk = chunks[i];
    if (result.status === 'rejected') {
      // Leave these ids unresolved (loading) rather than defaulting them to
      // "not in any list" — we don't know their membership, so they retry.
      console.error('Error fetching product membership:', result.reason);
      failedIds.push(...chunk);
      return;
    }
    // Default this chunk's ids to "not in any list" so a known-but-empty
    // product resolves to `false` (rather than the loading `null`) once data
    // is in, then overlay the actual memberships the response carried.
    for (const id of chunk) merged[id] = EMPTY_MEMBERSHIP;
    for (const [id, membership] of Object.entries(result.value.products)) {
      merged[id] = membership;
    }
  });

  return { merged, failedIds };
}

export type ProductMembershipApi = {
  isProductInList: (productId: string) => boolean | null;
  getMembership: (productId: string) => ProductMembership;
  fetchProductMembership: (productId: string) => Promise<void>;
  fetchBatchProductMembership: (productIds: string[]) => Promise<void>;
  setMembership: (productId: string, membership: ProductMembership) => void;
};

export function useProductMembership(
  shouldFetch: boolean,
  organizationId?: string
): ProductMembershipApi {
  const { data, mutate } = useSWR<MembershipMap>(
    shouldFetch ? ['product-membership', organizationId ?? ''] : null,
    null,
    { revalidateOnFocus: false }
  );

  // Ids already fetched or in-flight, so re-registration never re-requests them.
  const seenRef = useRef<Set<string>>(new Set());
  // Ids whose membership was set locally (optimistic save). An in-flight lookup
  // started before the save must not overwrite these with pre-save data.
  const savedRef = useRef<Set<string>>(new Set());

  // The active org owns the cache entry (it's part of the SWR key). When it
  // changes, SWR hands us a fresh `data`, so drop the per-org dedup tracking
  // synchronously here — before child effects re-register ids for the new org.
  const orgRef = useRef(organizationId);
  if (orgRef.current !== organizationId) {
    orgRef.current = organizationId;
    seenRef.current = new Set();
    savedRef.current = new Set();
  }

  const registerIds = useCallback(
    async (ids: string[]) => {
      if (!shouldFetch) return;
      const toFetch = ids.filter((id) => id && !seenRef.current.has(id));
      if (toFetch.length === 0) return;
      for (const id of toFetch) seenRef.current.add(id);

      try {
        const { merged, failedIds } = await fetchMembershipChunked(
          toFetch,
          organizationId
        );
        await mutate(
          (prev) => {
            const next = { ...(prev ?? {}) };
            for (const [id, membership] of Object.entries(merged)) {
              // Don't clobber a membership the user saved while this lookup
              // was in flight — the save is authoritative over stale data.
              if (!savedRef.current.has(id)) next[id] = membership;
            }
            return next;
          },
          { revalidate: false }
        );
        // Successful chunks stick; only the ids whose chunk failed are released
        // so they re-fetch the next time they're registered.
        for (const id of failedIds) seenRef.current.delete(id);
      } catch (error) {
        console.error('Error fetching product membership:', error);
        // Unexpected failure (not a per-chunk rejection) — allow a full retry.
        for (const id of toFetch) seenRef.current.delete(id);
      }
    },
    [shouldFetch, organizationId, mutate]
  );

  const fetchProductMembership = useCallback(
    async (productId: string) => {
      await registerIds([productId]);
    },
    [registerIds]
  );

  const fetchBatchProductMembership = useCallback(
    async (productIds: string[]) => {
      await registerIds(productIds);
    },
    [registerIds]
  );

  // null = membership not yet known (loading); false/true = resolved.
  const isProductInList = useCallback(
    (productId: string): boolean | null => {
      if (!shouldFetch) return null;
      const membership = data?.[productId];
      if (membership === undefined) return null;
      return membership.personal.length > 0;
    },
    [shouldFetch, data]
  );

  const getMembership = useCallback(
    (productId: string): ProductMembership =>
      data?.[productId] ?? EMPTY_MEMBERSHIP,
    [data]
  );

  // Reflect a just-saved change in the cache so the bookmark icon updates and
  // the modal re-seeds with the correct selection on reopen. We do NOT
  // revalidate: the add/remove API calls already succeeded, so this value is
  // authoritative, and the lookup endpoint is eventually consistent — a
  // background re-fetch can return the pre-save membership and clobber it.
  const setMembership = useCallback(
    (productId: string, membership: ProductMembership) => {
      // Mark as saved so an in-flight lookup's merge can't overwrite it.
      savedRef.current.add(productId);
      seenRef.current.add(productId);
      mutate((prev) => ({ ...(prev ?? {}), [productId]: membership }), {
        revalidate: false,
      });
    },
    [mutate]
  );

  return useMemo(
    () => ({
      isProductInList,
      getMembership,
      fetchProductMembership,
      fetchBatchProductMembership,
      setMembership,
    }),
    [
      isProductInList,
      getMembership,
      fetchProductMembership,
      fetchBatchProductMembership,
      setMembership,
    ]
  );
}
